import {createApi} from "@reduxjs/toolkit/query/react";
import {axiosBaseQuery} from "@/helpers/axiosBaseQuery";
import api from "@/store/api/api";
import {appendFormData} from "@/helpers/form";
import {salaryApi} from "@/store/api/v1/salary";

export const staffApi = createApi({
    reducerPath: 'staffApi',
    tagTypes: ['staff'],
    baseQuery: axiosBaseQuery({
        baseUrl: api.root,
    }),
    endpoints: build => ({
        getStaff: build.query({
            query: ({ queryOptions, ...params }) => ({
                url: api.staff.getStaff(),
                method: 'GET',
                params,
            }),
            serializeQueryArgs: ({ endpointName, queryArgs }) => {
                return [endpointName, queryArgs?.location_id]
            },
            merge: (currentCache, newItems, { arg }) => {
                if (arg.skip === 0 || arg.all) {
                    return newItems;
                }
                currentCache.push(...newItems.filter((newItem) => !currentCache.some((item) => item.id === newItem.id)))
            },
            forceRefetch: ({ currentArg, previousArg }) => {
                return currentArg !== previousArg;
            },
            providesTags: ['staff']
        }),
        getStaffById: build.query({
            query: ({ id, ...data }) => ({
                url: api.staff.getStaffById(id),
                method: 'GET',
                params: data,
            }),
        }),
        getStaffOptions: build.query({
            query: ({ queryOptions, ...params }) => ({
                url: api.staff.getStaff(),
                method: 'GET',
                params,
            }),
            serializeQueryArgs: ({ endpointName, queryArgs }) => {
                return [endpointName + 'Options', queryArgs?.location_id + 'Options']
            },
            merge: (currentCache, newItems, { arg }) => {
                if (arg.skip === 0) {
                    return newItems;
                }
                currentCache.push(...newItems.filter((newItem) => !currentCache.some((item) => item.id === newItem.id)))
            },
            forceRefetch: ({ currentArg, previousArg }) => {
                return currentArg !== previousArg;
            },
            providesTags: ['staff']
        }),
        getWholeStaffOptions: build.query({
            query: (data) => ({
                url: api.staff.getWholeStaff(),
                method: 'GET',
                params: data
            }),
            serializeQueryArgs: ({ endpointName, queryArgs }) => {
                return [endpointName + 'Options', queryArgs?.location_id + 'Options']
            },
            merge: (currentCache, newItems, { arg }) => {
                if (arg.skip === 0) {
                    return newItems;
                }
                currentCache.push(...newItems.filter((newItem) => !currentCache.some((item) => item.id === newItem.id)))
            },
            forceRefetch: ({ currentArg, previousArg }) => {
                return currentArg !== previousArg;
            },
            providesTags: ['staff']
        }),
        createStaff: build.mutation({
            query: (data) => {
                // SHOULD BE REFACTORED
                data.location_ids = data.location_ids.filter(v => v !== undefined && v !== 'undefined' && v !== null && v !== "");

                const formData = new FormData();
                appendFormData(formData, data);

                return {
                    url: api.staff.createStaff(),
                    method: 'POST',
                    headers: {
                        'Content-Type': 'multipart/form-data;'
                    },
                    body: formData,
                    formData: true
                };
            },
            async onQueryStarted(patch, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    dispatch(
                        staffApi.util.updateQueryData('getStaff', patch, (draft) => {
                            draft.unshift(data)
                        })
                    )
                } catch (error) {
                    console.error('Failed to update cache:', error)
                }
            },
        }),
        editStaff: build.mutation({
            query: ({id, ...data}) => {
                const formData = new FormData();
                appendFormData(formData, data);

                return {
                    url: api.staff.editStaff(id),
                    method: 'POST',
                    headers: {
                        'Content-Type': 'multipart/form-data;'
                    },
                    body: formData,
                    formData: true
                };
            },
            async onQueryStarted(patch, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    dispatch(
                        staffApi.util.updateQueryData('getStaff', {location_id: patch.location_ids[0]}, (draft) => {
                            const itemIndex = draft?.findIndex(item => item.id === patch.id)
                            if (itemIndex !== -1) {
                                draft[itemIndex] = data
                            }
                        })
                    )
                    dispatch(salaryApi.util.invalidateTags(['staff-services']));
                } catch(e) {
                    console.error(e)
                }
            },
        }),
        sendInvitation: build.mutation({
            query: ({id}) => {
                return {
                    url: api.staff.sendInvitation(id),
                    method: 'POST',
                };
            },
        }),
        deleteStaff: build.mutation({
            query: ({id, ...data}) => ({
                url: api.staff.deleteStaff(id),
                method: 'DELETE',
                body: data
            }),
            async onQueryStarted(patch, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled
                    dispatch(
                        staffApi.util.updateQueryData('getStaff', patch, (draft) => {
                            const index = draft.findIndex((staff) => staff.id === patch.id);
                            if (index !== -1) {
                                draft.splice(index, 1)
                            } else {
                                console.warn('Service not found in cache')
                            }
                        })
                    );
                } catch (e) {
                    console.error('Delete failed:', e)
                }
            },
        }),
    })
})

export const { useDeleteStaffMutation, useGetStaffQuery, useGetStaffByIdQuery, useLazyGetStaffQuery } = staffApi
